import React from "react";
import Container from "react-bootstrap/Container";
import {
  FaTruck,
  FaShoppingCart,
  FaHeart,
  FaUser,
  FaChevronDown,
  FaSearch,
  FaMapMarkerAlt,
  FaTimes,
  FaClipboardList,
  FaSignOutAlt,
  FaBirthdayCake,
  FaSpinner
} from "react-icons/fa";
import logo from "../../Asset/Cakerestrologo.png";
import "./Header.css";
import { useNavigate, Link } from "react-router-dom";
import { HiOutlineMenuAlt1 } from "react-icons/hi";
import { isLoggedIn, getCurrentUser, logout } from "../../services/AuthService";
import { getTotalItemsCount } from "../../services/CartService";
import { getWishlistCount } from "../../services/WishlistService";
import { GetAllProduct, QuickSearchProducts, slugify } from "../../services/ProductService";
import { PLACEHOLDER_IMAGE } from "../../services/urlService";
import { getCategoryTree, toMenuItems } from "../../services/homeService";
import { useLocation } from "../../context/LocationContext";
import { analytics } from "../../services/analytics";

const Header = () => {
  const { pincode, locationInfo, deliveredCity, checking, error, applyPincode, clearLocation } = useLocation();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [openMobileItem, setOpenMobileItem] = React.useState(null);
  const [isLocationPopupOpen, setIsLocationPopupOpen] = React.useState(false);
  const [pincodeInput, setPincodeInput] = React.useState('');
  const [locResult, setLocResult] = React.useState(null);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const [cartCount, setCartCount] = React.useState(0);
  const [wishlistCount, setWishlistCount] = React.useState(getWishlistCount());
  const [user, setUser] = React.useState(getCurrentUser());
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  const [allProducts, setAllProducts] = React.useState([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const [isSearchFocused, setIsSearchFocused] = React.useState(false);
  const searchDebounceRef = React.useRef(null);
  const [showProfileDropdown, setShowProfileDropdown] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const searchRef = React.useRef(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    GetAllProduct().then(setAllProducts).catch(() => {});
    getTotalItemsCount().then(setCartCount).catch(() => {});
    const updateCart = async () => {
      const count = await getTotalItemsCount();
      setCartCount(count);
    };
    const updateWishlist = () => setWishlistCount(getWishlistCount());
    window.addEventListener('cartUpdated', updateCart);
    window.addEventListener('wishlistUpdated', updateWishlist);
    return () => {
      window.removeEventListener('cartUpdated', updateCart);
      window.removeEventListener('wishlistUpdated', updateWishlist);
    };
  }, []);

  React.useEffect(() => {
    (async () => {
      try {
        const tree = await getCategoryTree();
        const menuItems = toMenuItems(tree);
        setItems([
          { label: 'Home', image: null, submenu: null, href: '/' },
          ...menuItems,
        ]);
      } catch (err) {
        console.error('Failed to load menu categories:', err);
      }
    })();
  }, []);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filterLocalProducts = React.useCallback((q, list) => {
    if (!q || !list || list.length === 0) return [];
    const lower = q.trim().toLowerCase();
    return list.filter(p => {
      const nameMatch = (p.name || '').toLowerCase().includes(lower);
      const catMatch = typeof p.category === 'string' && p.category.toLowerCase().includes(lower);
      return nameMatch || catMatch;
    }).slice(0, 8);
  }, []);

  const executeSearch = React.useCallback((q) => {
    const trimmed = q ? q.trim() : '';
    if (!trimmed) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    // 1. Instant local match for zero-latency feedback
    const localMatches = filterLocalProducts(trimmed, allProducts);
    setSearchResults(localMatches);

    // 2. Debounced live API search across entire database
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    setIsSearching(true);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const live = await QuickSearchProducts(trimmed, 8);
        if (Array.isArray(live) && live.length > 0) {
          setSearchResults(live);
        } else if (localMatches.length === 0) {
          setSearchResults([]);
        }
      } catch (err) {
        console.warn('Live search fallback to local:', err);
      } finally {
        setIsSearching(false);
      }
    }, 180);
  }, [allProducts, filterLocalProducts]);

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    setIsSearchFocused(true);
    executeSearch(q);
  };

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
    if (searchQuery.trim().length >= 1) {
      executeSearch(searchQuery);
    }
  };

  const handleSearchSelect = (product) => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearchFocused(false);
    setIsSearchOpen(false);
    if (product.slug) {
      navigate(`/product/${product.slug}`);
    }
  };

  const handleViewAllResults = () => {
    const trimmed = searchQuery.trim();
    if (trimmed) {
      analytics.viewSearchResults(trimmed);
      navigate(`/cakes?q=${encodeURIComponent(trimmed)}`);
      setSearchQuery('');
      setSearchResults([]);
      setIsSearchFocused(false);
      setIsSearchOpen(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (searchResults.length > 0) {
        handleSearchSelect(searchResults[0]);
      } else {
        handleViewAllResults();
      }
    } else if (e.key === 'Escape') {
      setIsSearchFocused(false);
      setIsSearchOpen(false);
    }
  };

  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 150) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    const handleCartUpdate = (e) => {
      setCartCount(e.detail.count);
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("cartUpdated", handleCartUpdate);

    // Check for user login on every render (or more robustly via events)
    setUser(getCurrentUser());

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("cartUpdated", handleCartUpdate);
    };
  }, []);

  const handleChange = (e) => {
    const value = e.target.value;
    if (value) {
      navigate(value);
    }
  };

  const toggleMobileItem = (index) => {
    setOpenMobileItem(openMobileItem === index ? null : index);
  };

  return (
    <header className={`sticky-header ${isScrolled ? "scrolled" : ""}`}>

      {/* TOP STRIP */}
      <div className="top-strip">
        <div className="top-strip-content">
          <span>Cake Restro presents a premium collection of eggless &amp; customized cakes, carefully crafted with the finest ingredients and rich flavours like Chocolate, Butterscotch, Red Velvet, Pineapple and more — thoughtfully made to make every celebration elegant and truly special. Enjoy complimentary delivery on selected orders, safe &amp; secure payments, FSSAI certified quality, and a 100% freshness promise — Order now and experience a truly luxurious dessert indulgence.FREE DELIVERY!!! Enjoy ₹0 shipping with our free delivery time slots</span>
        </div>
      </div>

      {/* MAIN HEADER */}
      <Container fluid className="main-header">
        <div className="main-row">

          {/* Mobile Menu Icon */}
          <div className="mobile-menu-btn" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            <HiOutlineMenuAlt1 />
          </div>

          {/* Logo */}
          <Link to="/" className="logo-container">
            <img src={logo} alt="Brand Logo" className="logo" />
          </Link>



          {/* Search */}
          <div className="search-box desktop-only" style={{ position: 'relative' }} ref={searchRef}>
            <div className="search-input-wrap">
              <input
                type="text"
                placeholder="Search cakes, flowers, gifts..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                onKeyDown={handleSearchKeyDown}
              />
              {isSearching ? (
                <FaSpinner className="search-icon search-spinner-rotating" />
              ) : (
                <FaSearch className="search-icon" />
              )}
            </div>
            {isSearchFocused && searchQuery.trim().length >= 1 && (
              <div className="search-results-dropdown">
                <div className="search-dropdown-header d-flex justify-content-between align-items-center">
                  <span>Related Products</span>
                  {isSearching ? (
                    <span className="text-muted"><FaSpinner className="search-spinner-rotating me-1" />Searching...</span>
                  ) : (
                    <span className="text-muted">{searchResults.length} found</span>
                  )}
                </div>
                {searchResults.length > 0 ? (
                  <>
                    <div className="search-dropdown-list">
                      {searchResults.map((p, i) => (
                        <div key={i} className="search-result-item" onClick={() => handleSearchSelect(p)}>
                          <img
                            loading="lazy"
                            src={p.image}
                            alt={p.name}
                            className="search-result-img"
                            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = PLACEHOLDER_IMAGE; }}
                          />
                          <div className="search-result-info">
                            <span className="search-result-name">{p.name}</span>
                            <div className="d-flex align-items-center gap-2 mt-1">
                              <span className="search-result-price">₹{Math.round(p.price || 0)}</span>
                              {p.category && (
                                <span className="search-result-category-badge">{p.category}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="search-dropdown-footer" onClick={handleViewAllResults}>
                      <span>View all results for "<strong>{searchQuery}</strong>" →</span>
                    </div>
                  </>
                ) : !isSearching ? (
                  <div className="search-dropdown-empty">
                    <p className="mb-2 text-muted" style={{ fontSize: '13px' }}>
                      No direct matches for "<strong>{searchQuery}</strong>"
                    </p>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary rounded-pill px-3"
                      style={{ fontSize: '12px', color: '#8a8f1d', borderColor: '#8a8f1d' }}
                      onClick={handleViewAllResults}
                    >
                      Search catalog for "{searchQuery}" →
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Icons - Simplified on mobile */}
          <div className="header-icons">
            <div className="search-toggle-mobile mobile-only" onClick={() => setIsSearchOpen(true)}>
              <FaSearch />
            </div>
            <div className="desktop-only"><FaTruck /> <span>Same Day</span></div>

            <div className="location desktop-only" onClick={() => { setPincodeInput(pincode); setLocResult(null); setIsLocationPopupOpen(true); }} style={{ cursor: 'pointer' }}>
              <FaMapMarkerAlt style={{ marginTop: 4, color: '#e53935' }} />
              <div className="location-text-wrap">
                <strong>{deliveredCity ? deliveredCity : 'Where to deliver?'}</strong>
                <p>{pincode ? `${pincode} · change` : 'Enter pincode'}</p>
              </div>
            </div>
            <div className="cart-icon desktop-only" onClick={() => navigate('/wishlist')} style={{ cursor: 'pointer', position: 'relative' }}>
              <FaHeart />
              {wishlistCount > 0 && <span className="cart-badge" style={{ background: '#ef4444' }}>{wishlistCount}</span>}
            </div>
            <div className="cart-icon desktop-only" onClick={() => navigate('/cart')} style={{ cursor: 'pointer', position: 'relative' }}>
              <FaShoppingCart />
              {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            </div>

            {isLoggedIn() ? (
              <div className="profile-dropdown" tabIndex={0} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowProfileDropdown(false); }}>
                <button className="profile-trigger" onClick={() => setShowProfileDropdown(!showProfileDropdown)}>
                  <span className="profile-avatar"><FaUser /></span>
                  <span className="user-name">{user?.username}</span>
                  <FaChevronDown className={`profile-chevron ${showProfileDropdown ? 'open' : ''}`} />
                </button>
                {showProfileDropdown && (
                  <div className="profile-dropdown-menu">
                    <div className="profile-dropdown-item profile-name-item">
                      <span className="profile-avatar"><FaUser /></span>
                      <span className="profile-full-name">{user?.username}</span>
                    </div>
                    <Link to="/my-orders" className="profile-dropdown-item" onClick={() => setShowProfileDropdown(false)}>
                      <FaClipboardList className="dropdown-item-icon" />
                      <span>My Orders</span>
                    </Link>
                    <Link to="/my-orders?tab=custom" className="profile-dropdown-item" onClick={() => setShowProfileDropdown(false)}>
                      <FaBirthdayCake className="dropdown-item-icon" />
                      <span>Custom Cake Orders</span>
                    </Link>
                    <button onClick={() => { logout(); setShowProfileDropdown(false); }} className="profile-dropdown-item logout-item">
                      <FaSignOutAlt className="dropdown-item-icon" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="user-icon">
                <button onClick={() => navigate('/login')} className="header-login-btn">
                  <FaUser /> <span>Login</span>
                </button>
              </div>
            )}

          </div>

        </div>
      </Container>

      {/* SECOND ROW NAVIGATION (Desktop only) */}
      <Container fluid className="secondary-header desktop-only">
        <nav className="desktop-nav">
          <ul className="menu secondary-menu">
            {items.map((item, index) => (
              <li key={index} className="nav-item">
                {item.href ? (
                  <Link to={item.href} className="nav-item-label" style={{ textDecoration: 'none', color: 'inherit' }}>
                    {item.label}
                  </Link>
                ) : (
                  <span className="nav-item-label">
                    {item.label} <FaChevronDown style={{ marginLeft: "4px", fontSize: "10px", marginTop: "2px" }} />
                  </span>
                )}
                {item.submenu && (
                  <div className="dropdown dropdown-mega position-absolute">
                    <div className="dropdown-content d-flex">
                      <div className="dropdown-links flex-grow-1 p-3">
                        <ul className="list-unstyled">
                          {item.submenu.map((sub, subIndex) => (
                            <li key={subIndex}>
                              <Link to={sub.href}>{sub.label}</Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {item.image && (
                        <div className="dropdown-image-wrap p-3">
                          <img loading="lazy" src={item.image} alt={item.label} style={{ width: '200px', borderRadius: '8px', objectFit: 'cover' }} />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </Container>

      {/* LOCATION POPUP MODAL */}
      {isLocationPopupOpen && (
        <div className="location-modal-overlay" onClick={() => setIsLocationPopupOpen(false)}>
          <div className="location-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="location-modal-close" onClick={() => setIsLocationPopupOpen(false)}>
              <FaTimes />
            </div>
            <div className="location-modal-header">
              <FaMapMarkerAlt className="location-modal-icon" />
              <h4>Let's Personalize Your Experience!</h4>
            </div>
            <p className="location-modal-subtitle">Enter your delivery pincode to check cake availability at your location.</p>

            <div className="location-modal-body">
              <div className="location-input-wrap">
                <input
                  type="text"
                  placeholder="Enter Receiver's pincode"
                  value={pincodeInput}
                  onChange={(e) => { setPincodeInput(e.target.value.replace(/\D/g, '').slice(0, 10)); setLocResult(null); }}
                  onKeyDown={async (e) => { if (e.key === 'Enter') { const r = await applyPincode(pincodeInput); setLocResult(r); } }}
                  autoFocus
                />
              </div>
              {checking && <p className="location-modal-msg">Checking availability...</p>}
              {!checking && locResult && locResult.deliverable && (
                <p className="location-modal-msg" style={{ color: '#2e7d32' }}>
                  ✓ Great! We deliver to {locationInfo?.city ? `${locationInfo.city}, ` : ''}{pincode || pincodeInput}.
                </p>
              )}
              {!checking && locResult && !locResult.deliverable && (
                <p className="location-modal-msg" style={{ color: '#e53935' }}>
                  ✗ {locResult.message || 'We currently do not deliver to this pincode.'}
                </p>
              )}
              <button className="location-continue-btn" onClick={async () => { const r = await applyPincode(pincodeInput); setLocResult(r); }}>
                {checking ? 'Checking...' : 'Check Delivery'}
              </button>
              {pincode && (
                <button className="location-continue-btn" style={{ background: '#555' }} onClick={() => { clearLocation(); setPincodeInput(''); setLocResult(null); setIsLocationPopupOpen(false); }}>
                  Remove Location
                </button>
              )}
              <button className="location-continue-btn" style={{ background: 'transparent', color: '#666', border: '1px solid #ddd', marginTop: 6 }} onClick={() => setIsLocationPopupOpen(false)}>
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MOBILE SEARCH POPUP */}
      {isSearchOpen && (
        <div className="search-popup-overlay">
          <div className="search-popup-content">
            <div className="search-popup-header">
              <div className="search-input-container">
                <FaSearch className="search-input-icon" />
                <input 
                  type="text" 
                  placeholder="Search for cakes, flowers, gifts..." 
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={(e) => { handleSearchKeyDown(e); if (e.key === 'Enter') setIsSearchOpen(false); }}
                  autoFocus 
                  className="mobile-search-input"
                />
              </div>
              <button className="search-popup-close-btn" onClick={() => setIsSearchOpen(false)}>
                <FaTimes />
              </button>
            </div>
            
            <div className="search-popup-body">
              {searchQuery.trim().length >= 1 ? (
                searchResults.length > 0 ? (
                  <div className="mobile-search-results">
                    {searchResults.map((p, i) => (
                      <div key={i} className="search-result-item" onClick={() => handleSearchSelect(p)}>
                        <img
                          loading="lazy"
                          src={p.image}
                          alt={p.name}
                          className="search-result-img"
                          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = PLACEHOLDER_IMAGE; }}
                        />
                        <div className="search-result-info">
                          <span className="search-result-name">{p.name}</span>
                          <div className="d-flex align-items-center gap-2 mt-1">
                            <span className="search-result-price">₹{Math.round(p.price || 0)}</span>
                            {p.category && (
                              <span className="search-result-category-badge">{p.category}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="search-dropdown-footer mt-2" onClick={handleViewAllResults}>
                      <span>View all results for "<strong>{searchQuery}</strong>" →</span>
                    </div>
                  </div>
                ) : !isSearching ? (
                  <div className="text-center py-4">
                    <p className="text-muted mb-2">No matching products found</p>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary rounded-pill px-3"
                      style={{ color: '#8a8f1d', borderColor: '#8a8f1d' }}
                      onClick={handleViewAllResults}
                    >
                      Search catalog for "{searchQuery}" →
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <FaSpinner className="search-spinner-rotating me-2" /> Searching cakes...
                  </div>
                )
              ) : (
                <div className="suggested-section">
                  <h5>Suggested for you</h5>
                  <div className="suggestion-chips">
                     {[
                      { label: "Birthday", href: "/birthday-cakes" },
                      { label: "Anniversary", href: "/anniversary-cakes" },
                      { label: "Photo Cakes", href: "/photo-cakes" },
                      { label: "Chocolate", href: "/chocolate-cakes" },
                      { label: "Flowers", href: "/flowers" },
                      { label: "Kids Cakes", href: "/kids-cakes" }
                    ].map((sug, i) => (
                      <Link key={i} to={sug.href} className="suggestion-chip" onClick={() => setIsSearchOpen(false)}>
                        {sug.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MOBILE OVERLAY - Only for mobile drawer */}
      {isMenuOpen && <div className="menu-overlay" onClick={() => setIsMenuOpen(false)}></div>}

      {/* MOBILE DRAWER */}
      <div className={`menu-bar ${isMenuOpen ? "open" : ""} mobile-only-drawer`}>
        <div className="drawer-header">
          <img src={logo} alt="Brand Logo" className="drawer-logo" />
          <div className="close-drawer" onClick={() => setIsMenuOpen(false)}>×</div>
        </div>
        <Container fluid>
          <ul className="menu">
            {items.map((item, index) => (
              <li
                key={index}
                className={`nav-item ${openMobileItem === index ? "mobile-open" : ""}`}
              >
                {item.href ? (
                  <Link to={item.href} className="nav-item-label" style={{ textDecoration: 'none', color: 'inherit' }} onClick={() => setIsMenuOpen(false)}>
                    {item.label}
                  </Link>
                ) : (
                  <span
                    className="nav-item-label"
                    onClick={() => toggleMobileItem(index)}
                  >
                    {item.label}
                    {item.submenu && (
                      <FaChevronDown className={`nav-chevron ${openMobileItem === index ? "rotated" : ""}`} />
                    )}
                  </span>
                )}

                {/* Submenu */}
                {item.submenu && (
                  <ul className="dropdown">
                    {item.submenu.map((sub, subIndex) => (
                      <li key={subIndex}>
                        <Link to={sub.href}>{sub.label}</Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </Container>
      </div>

    </header>
  );
};

export default Header;